import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Boxes,
  Plus,
  Search,
  Loader2,
  MapPin,
  Camera,
  Trash2,
  Edit3,
  Package,
  Building2,
  Car,
  Church,
  Laptop,
  Wrench,
  Layers,
  CircleDot,
  X,
  DollarSign,
  Calendar,
  History,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const CATEGORY_META = {
  TANAH_BANGUNAN: { label: "Tanah & Bangunan", icon: Building2, color: "#276AA3" },
  KENDARAAN: { label: "Kendaraan", icon: Car, color: "#8A5FA3" },
  PERALATAN_IBADAH: { label: "Peralatan Ibadah", icon: Church, color: "#3C8C4A" },
  PERLENGKAPAN_KANTOR: { label: "Perlengkapan Kantor", icon: Package, color: "#B58B00" },
  ELEKTRONIK: { label: "Elektronik", icon: Laptop, color: "#C8473D" },
  LAINNYA: { label: "Lainnya", icon: Layers, color: "#6B7280" },
};

const CONDITION_META = {
  BAIK: { label: "Baik", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PERLU_PERBAIKAN: {
    label: "Perlu Perbaikan",
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
  RUSAK: { label: "Rusak", color: "bg-rose-50 text-rose-700 border-rose-200" },
  TIDAK_TERPAKAI: {
    label: "Tidak Terpakai",
    color: "bg-slate-50 text-slate-600 border-slate-200",
  },
};

const MANAGE_ROLES = ["SUPER_ADMIN", "ADMIN_KLASIS", "BENDAHARA_KLASIS", "KETUA_KLASIS"];
const CREATE_ROLES = [
  ...MANAGE_ROLES,
  "SEKRETARIS_JEMAAT",
  "GEMBALA_JEMAAT",
  "BENDAHARA_JEMAAT",
];

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function formatCurrency(v) {
  if (v == null) return "-";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
}

export default function Aset() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCategory, setFilterCategory] = useState("ALL");
  const [filterCondition, setFilterCondition] = useState("ALL");
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailOpen, setDetailOpen] = useState(null);

  const canCreate = user && CREATE_ROLES.includes(user.role);
  const canManage = user && MANAGE_ROLES.includes(user.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory !== "ALL") params.append("category", filterCategory);
      if (filterCondition !== "ALL") params.append("condition", filterCondition);
      if (search.trim()) params.append("search", search.trim());
      const [{ data: r }, { data: s }, chRes] = await Promise.all([
        api.get(`/assets?${params.toString()}`),
        api.get("/assets/summary"),
        api.get("/churches").catch(() => ({ data: [] })),
      ]);
      setItems(r.items || []);
      setSummary(s);
      setChurches(chRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filterCategory, filterCondition]);

  const doSearch = (e) => {
    e.preventDefault();
    load();
  };

  const delItem = async (item) => {
    if (
      !window.confirm(
        `Hapus aset "${item.name}"? Data akan diarsipkan ke assets_archive dan bisa dipulihkan oleh Super Admin.`
      )
    )
      return;
    try {
      await api.delete(`/assets/${item.id}`);
      toast.success("Aset diarsipkan");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="page-aset">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
              Lainnya
            </div>
            <h1 className="font-heading font-black text-3xl mt-1 flex items-center gap-3">
              <Boxes className="h-8 w-8 text-primary" />
              Aset &amp; Inventaris
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Kelola aset fisik Klasis &amp; Jemaat: tanah, bangunan, kendaraan, peralatan ibadah,
              elektronik, dan lainnya. Dilengkapi histori mutasi lintas periode.
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() => {
                setEditing(null);
                setOpenForm(true);
              }}
              className="rounded-full"
              data-testid="btn-add-asset"
            >
              <Plus className="h-4 w-4 mr-2" /> Tambah Aset
            </Button>
          )}
        </div>

        {/* SUMMARY CARDS */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard
              label="Total Aset"
              value={summary.total}
              hint={`${Object.keys(summary.by_category).length} kategori`}
              icon={Boxes}
              testId="stat-total"
              color="text-primary"
            />
            <SummaryCard
              label="Nilai Perolehan"
              value={formatCurrency(summary.total_value)}
              hint="Estimasi total"
              icon={DollarSign}
              testId="stat-value"
              color="text-emerald-600"
              isText
            />
            <SummaryCard
              label="Kondisi Baik"
              value={summary.by_condition?.BAIK || 0}
              hint={`${
                summary.total ? Math.round(((summary.by_condition?.BAIK || 0) / summary.total) * 100) : 0
              }% dari total`}
              icon={CircleDot}
              testId="stat-baik"
              color="text-emerald-600"
            />
            <SummaryCard
              label="Perlu Perhatian"
              value={
                (summary.by_condition?.PERLU_PERBAIKAN || 0) +
                (summary.by_condition?.RUSAK || 0)
              }
              hint="Rusak / perlu perbaikan"
              icon={Wrench}
              testId="stat-rusak"
              color="text-amber-600"
            />
          </div>
        )}

        {/* CATEGORY CHIPS */}
        {summary && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilterCategory("ALL")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filterCategory === "ALL"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white border-border hover:bg-muted"
              }`}
              data-testid="chip-cat-ALL"
            >
              Semua ({summary.total})
            </button>
            {Object.entries(CATEGORY_META).map(([key, meta]) => {
              const cnt = summary.by_category?.[key] || 0;
              const Icon = meta.icon;
              const active = filterCategory === key;
              return (
                <button
                  key={key}
                  onClick={() => setFilterCategory(key)}
                  disabled={cnt === 0 && filterCategory !== key}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1.5 ${
                    active
                      ? "text-white border-transparent"
                      : "bg-white border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                  }`}
                  style={active ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
                  data-testid={`chip-cat-${key}`}
                >
                  <Icon className="h-3 w-3" />
                  {meta.label} ({cnt})
                </button>
              );
            })}
          </div>
        )}

        {/* FILTERS + SEARCH */}
        <div className="flex items-center gap-2 flex-wrap">
          <form onSubmit={doSearch} className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cari nama / SN / deskripsi…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="input-search-aset"
            />
          </form>
          <Select value={filterCondition} onValueChange={setFilterCondition}>
            <SelectTrigger className="w-44 rounded-full" data-testid="select-condition">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Kondisi</SelectItem>
              {Object.entries(CONDITION_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* LIST */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Memuat aset…
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-muted flex items-center justify-center">
                <Boxes className="h-7 w-7 text-muted-foreground" />
              </div>
              <div className="mt-4 font-heading font-black text-lg">Belum ada aset terdaftar</div>
              <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Mulai dengan mendaftarkan aset klasis dan jemaat. Klik <strong>Tambah Aset</strong>{" "}
                untuk membuat entri pertama.
              </div>
              {canCreate && (
                <Button
                  className="mt-4 rounded-full"
                  onClick={() => {
                    setEditing(null);
                    setOpenForm(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" /> Tambah Aset
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it) => {
              const cat = CATEGORY_META[it.category] || CATEGORY_META.LAINNYA;
              const cond = CONDITION_META[it.condition] || CONDITION_META.BAIK;
              const Icon = cat.icon;
              return (
                <Card
                  key={it.id}
                  className="group overflow-hidden hover:shadow-lg transition-all cursor-pointer border-border/60"
                  onClick={() => setDetailOpen(it)}
                  data-testid={`asset-card-${it.id}`}
                >
                  <div
                    className="relative h-32 flex items-center justify-center overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${cat.color}15 0%, ${cat.color}25 100%)`,
                    }}
                  >
                    {it.photo_base64 ? (
                      <img
                        src={it.photo_base64}
                        alt={it.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Icon className="h-14 w-14" style={{ color: cat.color }} />
                    )}
                    <Badge
                      className={`absolute top-2 right-2 text-[0.6rem] border ${cond.color}`}
                      variant="outline"
                    >
                      {cond.label}
                    </Badge>
                  </div>
                  <CardContent className="p-3.5 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div
                          className="text-[0.6rem] font-black uppercase tracking-widest"
                          style={{ color: cat.color }}
                        >
                          {cat.label}
                        </div>
                        <div
                          className="font-heading font-black text-sm mt-0.5 line-clamp-2"
                          title={it.name}
                        >
                          {it.name}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">
                        {it.church_short_name || "Klasis"}
                        {it.location_note ? ` · ${it.location_note}` : ""}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs pt-2 border-t border-border/60">
                      <div>
                        <span className="font-bold">{it.quantity || 1}</span>
                        <span className="text-muted-foreground"> {it.unit || "unit"}</span>
                      </div>
                      <div className="font-mono font-semibold text-primary">
                        {formatCurrency((it.acquisition_value || 0) * (it.quantity || 1))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* FORM DIALOG */}
        <AssetFormDialog
          open={openForm}
          onOpenChange={setOpenForm}
          editing={editing}
          churches={churches}
          onSaved={() => {
            setOpenForm(false);
            setEditing(null);
            load();
          }}
        />

        {/* DETAIL DIALOG */}
        <AssetDetailDialog
          open={!!detailOpen}
          onClose={() => setDetailOpen(null)}
          asset={detailOpen}
          canManage={canManage}
          onEdit={(a) => {
            setEditing(a);
            setDetailOpen(null);
            setOpenForm(true);
          }}
          onDelete={async (a) => {
            await delItem(a);
            setDetailOpen(null);
          }}
          onMutated={load}
        />
      </div>
    </AppShell>
  );
}

function SummaryCard({ label, value, hint, icon: Icon, testId, color, isText }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-border/60 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[0.65rem] uppercase tracking-widest font-bold text-muted-foreground">
          {label}
        </div>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div className={`mt-2 font-black ${color} ${isText ? "text-sm" : "text-3xl"}`}>{value}</div>
      <div className="text-[0.65rem] text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}

function AssetFormDialog({ open, onOpenChange, editing, churches, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    category: "PERALATAN_IBADAH",
    condition: "BAIK",
    church_id: "KLASIS",
    location_note: "",
    acquisition_date: "",
    acquisition_value: "",
    quantity: 1,
    unit: "unit",
    serial_number: "",
    description: "",
    photo_base64: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          name: editing.name || "",
          category: editing.category || "PERALATAN_IBADAH",
          condition: editing.condition || "BAIK",
          church_id: editing.church_id || "KLASIS",
          location_note: editing.location_note || "",
          acquisition_date: editing.acquisition_date || "",
          acquisition_value: editing.acquisition_value || "",
          quantity: editing.quantity || 1,
          unit: editing.unit || "unit",
          serial_number: editing.serial_number || "",
          description: editing.description || "",
          photo_base64: editing.photo_base64 || "",
        });
      } else {
        setForm({
          name: "",
          category: "PERALATAN_IBADAH",
          condition: "BAIK",
          church_id: "KLASIS",
          location_note: "",
          acquisition_date: "",
          acquisition_value: "",
          quantity: 1,
          unit: "unit",
          serial_number: "",
          description: "",
          photo_base64: "",
        });
      }
    }
  }, [open, editing]);

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Foto maksimal 2MB");
      return;
    }
    const b64 = await fileToBase64(f);
    setForm({ ...form, photo_base64: b64 });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nama aset wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        church_id: form.church_id === "KLASIS" ? null : form.church_id,
        acquisition_value: form.acquisition_value ? Number(form.acquisition_value) : null,
        quantity: Number(form.quantity) || 1,
      };
      if (editing) {
        await api.patch(`/assets/${editing.id}`, payload);
        toast.success("Aset diperbarui");
      } else {
        await api.post("/assets", payload);
        toast.success("Aset ditambahkan");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="asset-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Aset" : "Tambah Aset Baru"}</DialogTitle>
          <DialogDescription>
            Isi detail aset seperti nama, kategori, kondisi, lokasi (jemaat/klasis), jumlah, dan
            nilai perolehan. Foto opsional (JPG/PNG maksimal 2MB).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* PHOTO */}
          <div className="flex items-start gap-3">
            <div className="relative h-28 w-28 rounded-xl bg-muted overflow-hidden border-2 border-dashed border-border flex items-center justify-center shrink-0">
              {form.photo_base64 ? (
                <>
                  <img src={form.photo_base64} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
                    onClick={() => setForm({ ...form, photo_base64: "" })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </>
              ) : (
                <Camera className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1">
              <Label className="text-xs">Foto Aset</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handlePhoto}
                className="mt-1"
                data-testid="input-asset-photo"
              />
              <div className="text-[0.7rem] text-muted-foreground mt-1">
                Format JPG/PNG, maksimal 2MB. Opsional.
              </div>
            </div>
          </div>

          <div>
            <Label>Nama Aset *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Contoh: Sound System Utama"
              data-testid="input-asset-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Kategori *</Label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm({ ...form, category: v })}
              >
                <SelectTrigger data-testid="select-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kondisi</Label>
              <Select
                value={form.condition}
                onValueChange={(v) => setForm({ ...form, condition: v })}
              >
                <SelectTrigger data-testid="select-cond">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONDITION_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Lokasi (Jemaat / Klasis)</Label>
              <Select
                value={form.church_id}
                onValueChange={(v) => setForm({ ...form, church_id: v })}
              >
                <SelectTrigger data-testid="select-church">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="KLASIS">Kantor Klasis (Pusat)</SelectItem>
                  {churches.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.short_name || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Catatan Lokasi</Label>
              <Input
                value={form.location_note}
                onChange={(e) => setForm({ ...form, location_note: e.target.value })}
                placeholder="Contoh: Aula Utama, Rak Belakang"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Jumlah</Label>
              <Input
                type="number"
                min="1"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </div>
            <div>
              <Label>Satuan</Label>
              <Input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="unit / set / m²"
              />
            </div>
            <div>
              <Label>SN / Kode</Label>
              <Input
                value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                placeholder="Opsional"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Tanggal Perolehan</Label>
              <Input
                type="date"
                value={form.acquisition_date}
                onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Nilai Perolehan (Rp)</Label>
              <Input
                type="number"
                min="0"
                value={form.acquisition_value}
                onChange={(e) => setForm({ ...form, acquisition_value: e.target.value })}
                placeholder="1500000"
              />
            </div>
          </div>

          <div>
            <Label>Deskripsi</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Spesifikasi, kondisi tambahan, catatan penggunaan…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Batal
          </Button>
          <Button onClick={save} disabled={saving} data-testid="btn-save-asset">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {editing ? "Simpan Perubahan" : "Tambah Aset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetDetailDialog({ open, onClose, asset, canManage, onEdit, onDelete, onMutated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [mutForm, setMutForm] = useState({
    mutation_type: "REPAIR",
    condition_to: "BAIK",
    note: "",
    mutation_date: new Date().toISOString().slice(0, 10),
  });
  const [showMutForm, setShowMutForm] = useState(false);

  useEffect(() => {
    if (open && asset?.id) {
      setLoading(true);
      api
        .get(`/assets/${asset.id}`)
        .then(({ data }) => setDetail(data))
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
        .finally(() => setLoading(false));
    } else {
      setDetail(null);
      setShowMutForm(false);
    }
  }, [open, asset]);

  const submitMutation = async () => {
    if (!mutForm.mutation_date) {
      toast.error("Tanggal mutasi wajib");
      return;
    }
    setMutating(true);
    try {
      await api.post(`/assets/${asset.id}/mutations`, mutForm);
      toast.success("Mutasi tercatat");
      setShowMutForm(false);
      const { data } = await api.get(`/assets/${asset.id}`);
      setDetail(data);
      onMutated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setMutating(false);
    }
  };

  if (!asset) return null;
  const cat = CATEGORY_META[asset.category] || CATEGORY_META.LAINNYA;
  const cond = CONDITION_META[detail?.condition || asset.condition] || CONDITION_META.BAIK;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto p-0"
        data-testid="asset-detail"
      >
        <div
          className="relative h-40 flex items-center justify-center overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${cat.color}25 0%, ${cat.color}45 100%)`,
          }}
        >
          {(detail?.photo_base64 || asset.photo_base64) ? (
            <img
              src={detail?.photo_base64 || asset.photo_base64}
              alt={asset.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <cat.icon className="h-20 w-20" style={{ color: cat.color }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <div className="text-[0.6rem] font-black uppercase tracking-widest">{cat.label}</div>
            <div className="font-heading font-black text-xl mt-0.5 line-clamp-2">{asset.name}</div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {loading || !detail ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`border ${cond.color}`} variant="outline">
                  {cond.label}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <MapPin className="h-3 w-3 mr-1" />
                  {detail.church_short_name || "Klasis"}
                  {detail.location_note ? ` · ${detail.location_note}` : ""}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {detail.quantity} {detail.unit}
                </Badge>
                {detail.acquisition_value && (
                  <Badge variant="outline" className="text-xs font-mono">
                    {formatCurrency(detail.acquisition_value * (detail.quantity || 1))}
                  </Badge>
                )}
              </div>

              {detail.description && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">
                    Deskripsi
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{detail.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailField label="Serial / Kode" value={detail.serial_number} />
                <DetailField
                  label="Tanggal Perolehan"
                  value={detail.acquisition_date}
                  icon={Calendar}
                />
                <DetailField
                  label="Nilai Perolehan"
                  value={detail.acquisition_value ? formatCurrency(detail.acquisition_value) : null}
                  icon={DollarSign}
                />
                <DetailField label="Dibuat oleh" value={detail.created_by_name} />
              </div>

              {/* MUTATIONS */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <History className="h-3.5 w-3.5" /> Histori Mutasi (
                    {detail.mutations?.length || 0})
                  </div>
                  {canManage && !showMutForm && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full text-xs h-7"
                      onClick={() => setShowMutForm(true)}
                      data-testid="btn-add-mutation"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Catat Mutasi
                    </Button>
                  )}
                </div>

                {showMutForm && (
                  <div className="bg-muted/40 rounded-lg p-3 border border-border space-y-2 mb-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Jenis Mutasi</Label>
                        <Select
                          value={mutForm.mutation_type}
                          onValueChange={(v) => setMutForm({ ...mutForm, mutation_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="REPAIR">Perbaikan</SelectItem>
                            <SelectItem value="DAMAGE">Kerusakan</SelectItem>
                            <SelectItem value="DISPOSE">Penghapusan</SelectItem>
                            <SelectItem value="TRANSFER">Perpindahan</SelectItem>
                            <SelectItem value="REVALUE">Revaluasi</SelectItem>
                            <SelectItem value="ADD">Penambahan Unit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Tanggal</Label>
                        <Input
                          type="date"
                          value={mutForm.mutation_date}
                          onChange={(e) =>
                            setMutForm({ ...mutForm, mutation_date: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    {["DAMAGE", "REPAIR"].includes(mutForm.mutation_type) && (
                      <div>
                        <Label className="text-xs">Kondisi Setelah</Label>
                        <Select
                          value={mutForm.condition_to}
                          onValueChange={(v) => setMutForm({ ...mutForm, condition_to: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(CONDITION_META).map(([k, v]) => (
                              <SelectItem key={k} value={k}>
                                {v.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Catatan</Label>
                      <Textarea
                        rows={2}
                        value={mutForm.note}
                        onChange={(e) => setMutForm({ ...mutForm, note: e.target.value })}
                        placeholder="Detail mutasi…"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowMutForm(false)}
                        disabled={mutating}
                      >
                        Batal
                      </Button>
                      <Button size="sm" onClick={submitMutation} disabled={mutating}>
                        {mutating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                        Simpan
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {detail.mutations?.length ? (
                    detail.mutations.map((m) => (
                      <div
                        key={m.id}
                        className="bg-muted/30 rounded-md p-2 border border-border/60 text-xs"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-bold">{m.mutation_type}</div>
                          <div className="text-muted-foreground">{m.mutation_date}</div>
                        </div>
                        {m.note && (
                          <div className="mt-0.5 text-muted-foreground">{m.note}</div>
                        )}
                        {m.condition_to && (
                          <div className="mt-0.5 text-[0.7rem] text-primary">
                            → Kondisi: {CONDITION_META[m.condition_to]?.label || m.condition_to}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-muted-foreground italic text-center py-2">
                      Belum ada histori mutasi
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="p-4 border-t bg-muted/20">
          {canManage && (
            <>
              <Button
                variant="outline"
                onClick={() => onDelete(asset)}
                data-testid="btn-delete-asset"
                className="text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Hapus
              </Button>
              <Button variant="outline" onClick={() => onEdit(asset)} data-testid="btn-edit-asset">
                <Edit3 className="h-4 w-4 mr-1" /> Edit
              </Button>
            </>
          )}
          <Button onClick={onClose}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[0.65rem] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="mt-0.5 font-mono">{value || "-"}</div>
    </div>
  );
}
